import * as React from 'react';
import Navbar from './nav/Navbar';
import Footer from '../main/nav/Footer';
import { apiContext } from '../../Api';
import Swal from 'sweetalert2';

const columns = [
    { id: 'no', label: 'No' },
    { id: 'judul', label: 'Judul Publikasi' },
    { id: 'tipe', label: 'Jenis Publikasi' },
    { id: 'tanggal', label: 'Tanggal' },
    { id: 'kategori_kegiatan', label: 'Kategori Kegiatan' },
    { id: 'asal_data', label: 'Asal Data' },
    { id: 'quartile', label: 'Akreditasi' },
    { id: 'status', label: 'Status' },
    { id: 'a_klaim_bkd', label: 'Klaim BKD' },
    { id: 'option', label: 'Aksi' },
];

const icons = {
    search: <path d="m21 21-4.3-4.3M10.8 18a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z" />,
    filter: <path d="M4 5h16l-6 7v5l-4 2v-7L4 5Z" />,
    sync: <path d="M20 7v5h-5M4 17v-5h5M18.4 9A7 7 0 0 0 6.1 6.8M5.6 15A7 7 0 0 0 17.9 17.2" />,
    chevronLeft: <path d="m15 18-6-6 6-6" />,
    chevronRight: <path d="m9 18 6-6-6-6" />,
};

function Icon({ name, className = 'h-4 w-4' }) {
    return (
        <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            {icons[name]}
        </svg>
    );
}

function getPublicationType(item) {
    return item?.jenis_publikasi?.nama
        || '';
}

function getPublicationTitle(item) {
    return item?.judul || '';
}

function getPublicationDate(item) {
    return item?.tanggal || '';
}

function getPublicationCategory(item) {
    return item?.kategori_kegiatan || '';
}

function getPublicationSource(item) {
    return item?.asal_data || '';
}

function getPublicationQuartile(item) {
    const sintaAkred = item?.detail_publikasi?.sinta_akred;
    if (sintaAkred !== null && sintaAkred !== undefined && sintaAkred !== '') {
        return `S${sintaAkred}`;
    }
    const q = item?.quartile ?? item?.detail_publikasi?.quartile;
    if (q && q !== '0' && q !== 0) {
        return String(q).startsWith('Q') ? q : `Q${q}`;
    }
    return '';
}

function getPublicationBkdClaim(item) {
    return item?.a_klaim_bkd;
}

function getStatusLabel(status) {
    switch (status) {
        case 1:
            return <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-extrabold text-brand-green">Diterima</span>;
        case 2:
            return <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-extrabold text-yellow-600">Revisi</span>;
        case 3:
            return <span className="inline-flex rounded-full bg-rose-50 px-2.5 py-1 text-xs font-extrabold text-red-600">Ditolak</span>;
        default:
            return <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-extrabold text-slate-500">Draft</span>;
    }
}

function normalizeFilterValue(value) {
    return String(value || '').trim().toLowerCase();
}

function getBooleanValue(value) {
    return value === true || value === 1 || value === '1' || value === 'true' || value === 'Ya' || value === 'ya';
}

function getPublicationYear(item) {
    if (item?.tahun) {
        return item.tahun;
    }

    const dateValue = getPublicationDate(item);

    if (!dateValue) {
        return '';
    }

    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? '' : date.getFullYear();
}

function formatDate(value) {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(date);
}

function formatText(value) {
    if (!value) {
        return '-';
    }

    if (typeof value !== 'string') {
        return String(value);
    }

    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            return parsed.filter(Boolean).join(', ') || '-';
        }

        if (parsed && typeof parsed === 'object') {
            return Object.values(parsed).filter(Boolean).join(', ') || value;
        }
    } catch (error) {
        return value;
    }

    return value;
}

function getSearchText(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    if (Array.isArray(value)) {
        return value.map(getSearchText).join(' ');
    }

    if (typeof value === 'object') {
        return Object.values(value).map(getSearchText).join(' ');
    }

    if (typeof value === 'string') {
        try {
            return getSearchText(JSON.parse(value));
        } catch (error) {
            return value;
        }
    }

    return String(value);
}

export default function Data() {
    const { axios } = React.useContext(apiContext);
    const [data, setData] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const [search, setSearch] = React.useState('');
    const [typeFilter, setTypeFilter] = React.useState('all');
    const [yearFilter, setYearFilter] = React.useState('all');
    const [syncing, setSyncing] = React.useState(false);
    const [allData, setAllData] = React.useState([]);
    const [debouncedSearch, setDebouncedSearch] = React.useState(search);

    // RPU Publication Add States
    const [showAddForm, setShowAddForm] = React.useState(false);
    const [jenisPublikasiList, setJenisPublikasiList] = React.useState([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [judul, setJudul] = React.useState('');
    const [jenisPublikasiId, setJenisPublikasiId] = React.useState('');
    const [tanggal, setTanggal] = React.useState('');
    const [kategoriKegiatan, setKategoriKegiatan] = React.useState('');
    const [kategoriKegiatanSelect, setKategoriKegiatanSelect] = React.useState('');
    const [quartile, setQuartile] = React.useState('');
    const [sintaAkred, setSintaAkred] = React.useState('');
    const [namaJurnal, setNamaJurnal] = React.useState('');
    const [tautan, setTautan] = React.useState('');
    const [aKlaimBkd, setAKlaimBkd] = React.useState(false);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(handler);
    }, [search]);

    React.useEffect(() => {
        const fetchJenisPublikasi = async () => {
            try {
                const response = await axios.get('/jenis-publikasi');
                if (response.data) {
                    setJenisPublikasiList(response.data);
                }
            } catch (error) {
                console.error('Gagal mengambil jenis publikasi', error);
            }
        };
        fetchJenisPublikasi();
    }, [axios]);

    const fetchData = async () => {
        const response = await axios.get('/data', {
            params: {
                search: debouncedSearch,
                type: typeFilter,
                year: yearFilter
            }
        });
        if (response.data) {
            setData(response.data);
            if (!debouncedSearch && typeFilter === 'all' && yearFilter === 'all') {
                setAllData(response.data);
            }
        }
    };

    const syncData = async () => {
        setSyncing(true);

        try {
            Swal.fire({
                title: 'Mohon tunggu',
                text: 'Sedang menarik data publikasi dan detail penulis dari SISTER.',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            const response = await axios.post('/data/sync');

            Swal.fire({
                icon: response.data?.configured === false ? 'info' : 'success',
                title: response.data?.configured === false ? 'Sync diterima' : 'Sync berhasil',
                text: response.data?.message || 'Request sync berhasil dipanggil.',
                timer: 1800,
                showConfirmButton: false,
            });

            fetchData();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Sync gagal',
                text: error?.response?.data?.message || 'Tidak dapat memanggil sync data.',
            });
        } finally {
            setSyncing(false);
        }
    };

    const resetForm = () => {
        setJudul('');
        setJenisPublikasiId('');
        setTanggal('');
        setKategoriKegiatan('');
        setKategoriKegiatanSelect('');
        setQuartile('');
        setSintaAkred('');
        setNamaJurnal('');
        setTautan('');
        setAKlaimBkd(false);
        setShowAddForm(false);
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);

        try {
            Swal.fire({
                title: 'Mohon tunggu',
                text: 'Sedang menyimpan data publikasi RPU.',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            const payload = {
                judul,
                jenis_publikasi_id: jenisPublikasiId,
                tanggal,
                kategori_kegiatan: kategoriKegiatan || '-',
                quartile: quartile || null,
                sinta_akred: sintaAkred ? parseInt(sintaAkred) : null,
                nama_jurnal: namaJurnal || null,
                tautan: tautan || null,
                a_klaim_bkd: aKlaimBkd,
            };

            await axios.post('/data', payload);

            Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Data publikasi RPU berhasil disimpan.',
                timer: 1800,
                showConfirmButton: false,
            });

            resetForm();
            fetchData();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Gagal menyimpan',
                text: error?.response?.data?.message || 'Terjadi kesalahan saat menyimpan data publikasi RPU.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Apakah Anda yakin?',
            text: 'Data publikasi RPU ini akan dihapus secara permanen!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ya, hapus!',
            cancelButtonText: 'Batal',
            confirmButtonColor: '#d33',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    Swal.fire({
                        title: 'Mohon tunggu',
                        text: 'Sedang menghapus data publikasi...',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                    });

                    await axios.delete(`/publikasi-delete/${id}`);

                    Swal.fire({
                        icon: 'success',
                        title: 'Berhasil',
                        text: 'Data publikasi berhasil dihapus.',
                        timer: 1500,
                        showConfirmButton: false
                    });

                    fetchData();
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Gagal',
                        text: error?.response?.data?.message || 'Terjadi kesalahan saat menghapus data.'
                    });
                }
            }
        });
    };

    React.useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, typeFilter, yearFilter]);

    const typeOptions = React.useMemo(() => {
        const options = new Map();

        allData.forEach((item) => {
            const label = String(getPublicationType(item) || '').trim();
            const key = normalizeFilterValue(label);

            if (label && !options.has(key)) {
                options.set(key, label);
            }
        });

        return [...options.entries()]
            .sort((a, b) => a[1].localeCompare(b[1]))
            .map(([value, label]) => ({ value, label }));
    }, [allData]);

    const yearOptions = React.useMemo(() => {
        return [...new Set(allData.map((item) => String(getPublicationYear(item) || '').trim()).filter(Boolean))]
            .sort((a, b) => Number(b) - Number(a));
    }, [allData]);

    const filteredData = data;

    const pageCount = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
    const safePage = Math.min(page, pageCount - 1);
    const visibleRows = filteredData.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

    React.useEffect(() => {
        setPage(0);
    }, [search, typeFilter, yearFilter, rowsPerPage]);

    React.useEffect(() => {
        if (typeFilter !== 'all' && !typeOptions.some((item) => item.value === typeFilter)) {
            setTypeFilter('all');
        }
    }, [typeFilter, typeOptions]);

    React.useEffect(() => {
        if (yearFilter !== 'all' && !yearOptions.includes(yearFilter)) {
            setYearFilter('all');
        }
    }, [yearFilter, yearOptions]);

    const resetFilters = () => {
        setSearch('');
        setTypeFilter('all');
        setYearFilter('all');
    };

    const hasActiveFilter = search.trim() || typeFilter !== 'all' || yearFilter !== 'all';

    return (
        <div className="min-h-screen bg-brand-soft text-brand-navy md:flex">
            <Navbar title="Data Publikasi" />

            <main className="min-w-0 flex-1 pt-20 md:pt-24 mt-3">
                <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                    <section className="relative overflow-hidden rounded-lg border border-slate-200 bg-white p-5 shadow-soft sm:p-6">
                        {/* Decorative Gold Line */}
                        <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-90"></div>

                        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <span className="inline-flex items-center gap-2 rounded-full bg-brand-gold/10 px-3 py-1.5 text-xs font-extrabold uppercase text-yellow-600 border border-brand-gold/20 shadow-sm">
                                    <Icon name="filter" className="h-3.5 w-3.5" />
                                    Kelola Data
                                </span>
                                <h1 className="mt-4 font-display text-3xl font-extrabold">Data Publikasi</h1>
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                    Data ini membaca tabel publikasi hasil sinkronisasi SISTER.
                                </p>
                            </div>

                            <div className="flex gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => setShowAddForm(!showAddForm)}
                                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-brand-blue/30 px-5 text-sm font-extrabold text-brand-blue shadow-sm transition hover:bg-brand-blue/5"
                                >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                    </svg>
                                    {showAddForm ? 'Tutup Form' : 'Tambah Publikasi RPU'}
                                </button>
                                <button
                                    type="button"
                                    onClick={syncData}
                                    disabled={syncing}
                                    className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-gold px-5 text-sm font-extrabold text-brand-navy shadow-sm transition hover:bg-yellow-300 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
                                >
                                    <Icon name="sync" className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                                    {syncing ? 'Sync...' : 'Sync Data'}
                                </button>
                            </div>
                        </div>

                        {showAddForm && (
                            <form onSubmit={handleFormSubmit} className="mt-6 border border-slate-200 rounded-lg p-5 bg-slate-50/50">
                                <h3 className="text-lg font-bold mb-4 text-brand-navy border-b pb-2">Tambah Data Publikasi (RPU)</h3>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Judul Publikasi *</label>
                                        <input
                                            type="text"
                                            required
                                            value={judul}
                                            onChange={(e) => setJudul(e.target.value)}
                                            placeholder="Masukkan judul publikasi"
                                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Jenis Publikasi *</label>
                                        <select
                                            required
                                            value={jenisPublikasiId}
                                            onChange={(e) => setJenisPublikasiId(e.target.value)}
                                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        >
                                            <option value="">Pilih jenis publikasi...</option>
                                            {jenisPublikasiList.map((item) => (
                                                <option key={item.id} value={item.id}>{item.nama}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Tanggal Publikasi *</label>
                                        <input
                                            type="date"
                                            required
                                            value={tanggal}
                                            onChange={(e) => setTanggal(e.target.value)}
                                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Kategori Kegiatan</label>
                                        <select
                                            value={kategoriKegiatanSelect}
                                            onChange={(e) => {
                                                setKategoriKegiatanSelect(e.target.value);
                                                if (e.target.value !== 'custom') {
                                                    setKategoriKegiatan(e.target.value);
                                                } else {
                                                    setKategoriKegiatan('');
                                                }
                                            }}
                                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        >
                                            <option value="">Pilih kategori...</option>
                                            <option value="Hasil penelitian atau hasil pemikiran yang Dipresentasikan secara oral dan dimuat dalam prosiding yang dipublikasikan (ber ISSN/ISBN): Internasional terindeks Scopus, IEEE Explore, SPIE">Prosiding Internasional Scopus/IEEE/SPIE</option>
                                            <option value="Hasil penelitian/pemikiran yang dipresentasikan secara oral dan dimuat dalam prosiding yang dipublikasikan (ber ISBN/ISSN) dalam seminar internasional">Prosiding Internasional</option>
                                            <option value="Hasil penelitian atau hasil pemikiran yang Dipresentasikan secara oral dan dimuat dalam prosiding yang dipublikasikan (ber ISSN/ISBN): Nasional">Prosiding Nasional</option>
                                            <option value="Hasil penelitian/pemikiran yang dipublikasikan dalam bentuk jurnal internasional bereputasi">Jurnal Internasional Bereputasi</option>
                                            <option value="Hasil penelitian/pemikiran yang dipublikasikan dalam bentuk jurnal nasional terakreditasi Kemenristekdikti">Jurnal Nasional Terakreditasi</option>
                                            <option value="Hasil penelitian/pemikiran yang dipublikasikan dalam bentuk jurnal nasional">Jurnal Nasional</option>
                                            <option value="custom">Input Kustom...</option>
                                        </select>
                                    </div>
                                    {kategoriKegiatanSelect === 'custom' && (
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-bold text-slate-700 mb-1">Kategori Kegiatan Kustom *</label>
                                            <input
                                                type="text"
                                                required
                                                value={kategoriKegiatan}
                                                onChange={(e) => setKategoriKegiatan(e.target.value)}
                                                placeholder="Masukkan deskripsi kategori kegiatan"
                                                className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Nama Jurnal / Seminar</label>
                                        <input
                                            type="text"
                                            value={namaJurnal}
                                            onChange={(e) => setNamaJurnal(e.target.value)}
                                            placeholder="Masukkan nama jurnal atau nama seminar"
                                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Tautan Publikasi</label>
                                        <input
                                            type="url"
                                            value={tautan}
                                            onChange={(e) => setTautan(e.target.value)}
                                            placeholder="https://example.com/publikasi"
                                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Akreditasi / Quartile</label>
                                        <select
                                            value={quartile}
                                            onChange={(e) => setQuartile(e.target.value)}
                                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        >
                                            <option value="">Pilih Quartile...</option>
                                            <option value="Q1">Q1</option>
                                            <option value="Q2">Q2</option>
                                            <option value="Q3">Q3</option>
                                            <option value="Q4">Q4</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-1">Sinta Akreditasi (1-6)</label>
                                        <select
                                            value={sintaAkred}
                                            onChange={(e) => setSintaAkred(e.target.value)}
                                            className="h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        >
                                            <option value="">Pilih Sinta...</option>
                                            <option value="1">S1</option>
                                            <option value="2">S2</option>
                                            <option value="3">S3</option>
                                            <option value="4">S4</option>
                                            <option value="5">S5</option>
                                            <option value="6">S6</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mt-4 flex items-center">
                                    <input
                                        type="checkbox"
                                        id="aKlaimBkd"
                                        checked={aKlaimBkd}
                                        onChange={(e) => setAKlaimBkd(e.target.checked)}
                                        className="h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                                    />
                                    <label htmlFor="aKlaimBkd" className="ml-2 text-sm font-bold text-slate-700 select-none">
                                        Klaim BKD
                                    </label>
                                </div>
                                <div className="mt-5 flex justify-end gap-2 border-t pt-4">
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 transition hover:bg-slate-100"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="h-10 px-5 rounded-lg bg-brand-navy text-white text-sm font-bold transition hover:bg-slate-800 disabled:bg-slate-300 disabled:text-slate-500"
                                    >
                                        {submitting ? 'Menyimpan...' : 'Simpan Publikasi RPU'}
                                    </button>
                                </div>
                            </form>
                        )}

                        <div className="mt-6 grid gap-3 lg:grid-cols-[1fr_220px_180px_120px]">
                            <label className="relative block">
                                <span className="sr-only">Search publikasi</span>
                                <Icon name="search" className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Cari judul, jenis, kategori, asal data..."
                                    className="h-11 w-full rounded-lg border border-slate-200 bg-white pl-11 pr-4 text-sm font-semibold outline-none transition placeholder:text-slate-400 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                />
                            </label>

                            <label>
                                <span className="sr-only">Filter jenis publikasi</span>
                                <select
                                    value={typeFilter}
                                    onChange={(event) => setTypeFilter(event.target.value)}
                                    className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                >
                                    <option value="all">Semua jenis</option>
                                    {typeOptions.map((item) => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span className="sr-only">Filter tahun</span>
                                <select
                                    value={yearFilter}
                                    onChange={(event) => setYearFilter(event.target.value)}
                                    className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                >
                                    <option value="all">Semua tahun</option>
                                    {yearOptions.map((item) => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                            </label>


                        </div>

                        <div className="mt-5 flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                            <span>
                                Menampilkan <strong className="text-brand-navy">{visibleRows.length}</strong> dari <strong className="text-brand-navy">{filteredData.length}</strong> data
                            </span>
                            <label className="inline-flex items-center gap-2 font-semibold">
                                Baris
                                <select
                                    value={rowsPerPage}
                                    onChange={(event) => setRowsPerPage(Number(event.target.value))}
                                    className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                >
                                    {[20, 50, 100].map((item) => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
                            <div className="overflow-x-auto">
                                <table className="min-w-[980px] w-full divide-y divide-slate-200 text-left text-sm">
                                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                                        <tr>
                                            {columns.map((column) => (
                                                <th key={column.id} scope="col" className="px-4 py-3 font-extrabold">
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {visibleRows.length ? visibleRows.map((row, index) => (
                                            <tr key={row.id} className="align-top transition hover:bg-brand-soft/70">
                                                <td className="px-4 py-4 font-bold text-slate-500">
                                                    {safePage * rowsPerPage + index + 1}
                                                </td>
                                                <td className="max-w-md px-4 py-4 font-bold text-brand-navy">
                                                    {getPublicationTitle(row) || '-'}
                                                </td>
                                                <td className="px-4 py-4">
                                                    <span className="inline-flex rounded-full bg-brand-soft px-3 py-1 text-xs font-extrabold text-brand-green">
                                                        {getPublicationType(row) || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 font-semibold text-slate-600">{formatDate(getPublicationDate(row))}</td>
                                                <td className="max-w-sm px-4 py-4 text-slate-600">
                                                    {formatText(getPublicationCategory(row))}
                                                </td>
                                                <td className="px-4 py-4 font-semibold text-slate-600">{getPublicationSource(row) || '-'}</td>
                                                <td className="px-4 py-4 font-semibold text-slate-600">{getPublicationQuartile(row) || '-'}</td>
                                                <td className="px-4 py-4">
                                                    {row.asal_data !== 'RPU' ? (
                                                        <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-extrabold text-brand-green">Diterima</span>
                                                    ) : (
                                                        <>
                                                            {getStatusLabel(row.status)}
                                                            {row.status === 2 && row.komentar && (
                                                                <div className="text-[10px] text-slate-500 mt-1 max-w-xs break-words italic leading-normal">
                                                                    Catatan: {row.komentar}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </td>
                                                <td className="px-4 py-4">
                                                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-extrabold ${getBooleanValue(getPublicationBkdClaim(row)) ? 'bg-emerald-50 text-brand-green' : 'bg-slate-100 text-slate-500'}`}>
                                                        {getBooleanValue(getPublicationBkdClaim(row)) ? 'Ya' : 'Tidak'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4">
                                                    {row.asal_data === 'RPU' ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDelete(row.id)}
                                                            className="inline-flex items-center justify-center rounded-lg bg-rose-50 hover:bg-rose-100 p-2 text-xs font-bold text-red-600 border border-rose-200 transition"
                                                        >
                                                            Hapus
                                                        </button>
                                                    ) : (
                                                        <span className="text-xs font-semibold text-slate-400">-</span>
                                                    )}
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={columns.length} className="px-4 py-12 text-center text-sm font-semibold text-slate-500">
                                                    Data tidak ditemukan.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <span className="text-sm font-semibold text-slate-500">
                                Halaman {safePage + 1} dari {pageCount}
                            </span>
                            <div className="inline-flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPage((value) => Math.max(0, value - 1))}
                                    disabled={safePage === 0}
                                    className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-brand-navy transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <Icon name="chevronLeft" />
                                    Prev
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}
                                    disabled={safePage >= pageCount - 1}
                                    className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-brand-navy transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Next
                                    <Icon name="chevronRight" />
                                </button>
                            </div>
                        </div>
                    </section>

                    <div className="mt-8 rounded-lg bg-brand-navy text-white">
                        <Footer />
                    </div>
                </div>
            </main>
        </div>
    );
}
